import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { updateConfigurationOptions } from 'reducers/device/actions';
import Spinner from 'components/Spinner';
import {
  subscribe,
  unsubscribe,
  POLL_CONFIGURATION
} from 'reducers/polling/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';
import { durationFormatter } from 'utils/format';
import { FlexPage } from 'components/CommonStyles';
import Table from 'components/Table';
import EditConfigurationModal from 'components/Modals/EditConfigurationModal';

const columnDefs = [
  {
    headerName: 'Description',
    field: 'description'
  },
  {
    headerName: 'Value',
    field: 'value',
    cellRenderer: ({ value }) =>
      (Number.isInteger(parseInt(value)) &&
        durationFormatter(parseInt(value * 100))) ||
      'Not set'
  }
];

const ConfigurationPage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  height: 100%;
`;

const StyledForm = styled.form`
  overflow-y: auto;
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;

const EmptySubMenuBar = styled.div`
  width: 100%;
  height: 45px;
  background-color: white;
`;

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const Configuration = () => {
  const edevListPollRate = useSelector(
    state => state.device.configuration.edevListPollRate
  );
  const mupListPollRate = useSelector(
    state => state.device.configuration.mupListPollRate
  );
  const showAcctMenuDropdown = useSelector(
    state => state.customer.showAcctMenuDropdown
  );
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  //Get the configuration options
  //Set loading to false when done.
  useEffect(() => {
    const fetchConfigOptions = async () => {
      await dispatch(updateConfigurationOptions());
      setIsLoading(false);
    };
    fetchConfigOptions();
  }, []);

  //Poll for devices data
  useEffect(() => {
    const subscriberName = 'Configuration';
    subscribe({
      subscriber: subscriberName,
      pollTypes: [
        {
          type: POLL_CONFIGURATION
        }
      ],
      frequency: pollRate
    });

    //Stop polling during clean-up
    return () => {
      unsubscribe(subscriberName);
    };
  }, []);

  //Non-Editing display data
  const configData = useMemo(
    () => [
      {
        description: '2030.5 End Device List Poll Rate',
        value: edevListPollRate,
        id: 'edevListPollRate'
      },
      {
        description: '2030.5 Mirror Usage Poll Rate',
        value: mupListPollRate,
        id: 'mupListPollRate'
      }
    ],
    [edevListPollRate, mupListPollRate]
  );

  const toggleMenu = () => {
    if (showAcctMenuDropdown) {
      toggleAccountMenuDropdown(false);
    }
  };

  const handleEdit = e => {
    e.preventDefault();
    if (!showModal) {
      setShowModal(true);
    }
  };

  return (
    <FlexPage>
      {isLoading ? (
        <Spinner message="Loading Configuration..." />
      ) : (
        <ConfigurationPage onClick={toggleMenu}>
          <EmptySubMenuBar />
          <StyledForm>
            <Table
              bigTitle="Device Configuration"
              columnDefs={columnDefs}
              rowData={configData}
              height="600px"
              width="1000px"
              showEditButton={true}
              handleEdit={handleEdit}
            />
          </StyledForm>
        </ConfigurationPage>
      )}
      {showModal && (
        <EditConfigurationModal
          rowData={configData}
          closeModal={() => setShowModal(false)}
        />
      )}
    </FlexPage>
  );
};

export default Configuration;
